import {
  Avatar,
  type AvatarProps,
  Col,
  Icon,
  Spinner,
  Tappable,
  Text,
  useTheme,
  type ViewStyle,
} from "@rally/ui-kit";
import { useRef, useState } from "react";
import { say } from "../localization/say.ts";
import { why } from "../net/why.ts";
import { dropAFace, useFace, wearAFace } from "./face.ts";

export function FaceYouCanChange({
  name,
  avatar,
  subject,
  size = 88,
  tone,
  onChanged,
  onWrong,
  testID = "face",
  removeTestID = "drop-face",
}: {
  name: string;

  avatar: string | null;

  subject?: string;
  size?: number;

  tone?: AvatarProps["tone"];

  onChanged: (avatar: string | null) => void;

  onWrong: (said: string | null) => void;
  testID?: string;
  removeTestID?: string;
}) {
  const theme = useTheme();
  const face = useFace(avatar);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [dropping, setDropping] = useState(false);

  const picker = useRef<HTMLInputElement | null>(null);

  async function put(file: File): Promise<void> {
    setBusy(true);
    onWrong(null);
    try {
      onChanged(await wearAFace(file, subject));
    } catch (cause) {
      onWrong(why(cause, "add"));
    } finally {
      setBusy(false);
    }
  }

  async function drop(): Promise<void> {
    setBusy(true);
    onWrong(null);
    try {
      await dropAFace(subject);
      onChanged(null);
    } catch (cause) {
      onWrong(why(cause, "add"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Col>
      <Tappable
        onPress={() => picker.current?.click()}
        onHoverIn={() => setOver(true)}
        onHoverOut={() => setOver(false)}
        accessibilityRole="button"
        accessibilityLabel={avatar ? say("you.changeFace") : say("you.addFace")}
        testID={testID}
        {...({
          onDragOver: (event: DragEvent) => {
            event.preventDefault();
            setDropping(true);
          },
          onDragLeave: () => setDropping(false),
          onDrop: (event: DragEvent) => {
            event.preventDefault();
            setDropping(false);
            const file = event.dataTransfer?.files?.[0];
            if (file) void put(file);
          },
        } as object)}
        style={
          {
            borderRadius: 999,

            borderWidth: 2,
            borderColor: dropping ? theme.color.accent : "transparent",
            padding: 2,
          } as ViewStyle
        }
      >
        <Col style={{ borderRadius: 999, overflow: "hidden" }}>
          <Avatar name={name} src={face} size={size} {...(tone ? { tone } : {})} />
          <Col
            style={
              {
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                backgroundColor: "rgba(6,10,16,0.62)",
                opacity: over || busy || dropping ? 1 : 0,
                transitionProperty: "opacity",
                transitionDuration: theme.preferReduced ? "0ms" : "140ms",
              } as ViewStyle
            }
          >
            {busy ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Icon name="camera" size={Math.min(20, size * 0.28)} color="text.primary" />
                {size >= 64 ? (
                  <Text typeName="caption" color="text.primary">
                    {avatar ? say("you.changeFaceShort") : say("you.addFaceShort")}
                  </Text>
                ) : null}
              </>
            )}
          </Col>
        </Col>
      </Tappable>
      <Col style={{ flexDirection: "row", justifyContent: "center", gap: theme.space[3], marginTop: 6 }}>
        <Tappable
          onPress={() => picker.current?.click()}
          accessibilityRole="button"
          testID={`${removeTestID}-change`}
          style={{
            minHeight: 40,
            flexDirection: "row",
            alignItems: "center",
            gap: theme.space[1],
            paddingHorizontal: theme.space[2],
          }}
        >
          <Icon name="camera" size={13} color="text.tertiary" />
          <Text typeName="caption" color="text.tertiary">
            {avatar ? say("you.changeFaceShort") : say("you.addFaceShort")}
          </Text>
        </Tappable>
        {avatar ? (
          <Tappable
            onPress={() => void drop()}
            accessibilityRole="button"
            testID={removeTestID}
            style={{
              minHeight: 40,
              flexDirection: "row",
              alignItems: "center",
              gap: theme.space[1],
              paddingHorizontal: theme.space[2],
            }}
          >
            <Icon name="trash" size={13} color="text.tertiary" />
            <Text typeName="caption" color="text.tertiary">
              {say("you.dropFace")}
            </Text>
          </Tappable>
        ) : null}
      </Col>

      <input
        ref={picker}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void put(file);
          event.target.value = "";
        }}
      />
    </Col>
  );
}
